import React from 'react';
import { Box, Container, Typography, Stack, Button, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { text, border } from '../theme/colors';

const DEEP_TEAL = '#185451';
const TEAL_DARK = '#134e4a';
const FONT = "'Figtree', system-ui, -apple-system, Segoe UI, Roboto, sans-serif";

const pageContent = {
  about: {
    title: 'About Eqaya',
    intro: 'Eqaya is a trust platform that helps Zimbabweans abroad build, renovate, and verify property back home in a safer, more transparent, and more organized way.',
    sections: [
      {
        heading: 'Why we exist',
        body: 'Eqaya is a trust platform for diaspora builders. Too many people building from abroad face challenges with trust, integrity, fake progress updates, poor-quality work, unclear quotes, and unreliable contractors. Eqaya helps fix that by bringing verified contractors, independent work verification, milestone tracking, escrow protection, and payment controls into one platform. Your money is kept in escrow, and you release payment only when you are satisfied with the work.'
      },
      {
        heading: 'What we do',
        body: 'Clients can describe a project, compare contractor options, request independent site checks, and release payments only when work has been confirmed. Contractors and verifiers get a clearer way to win work, document progress, and build reputation through transparent delivery.'
      },
      {
        heading: 'Who we serve',
        body: 'Eqaya is designed for Zimbabwean families, homeowners, and investors in the diaspora who want a safer, clearer way to build back home. Whether you are starting a new build, renovating, or checking progress from afar, Eqaya helps you stay connected to the work, the people doing it, and the payments behind it.'
      }
    ],
    cta: 'Start your project'
  },
  contact: {
    title: 'Contact Us',
    intro: 'Have a question, need help choosing the right service, or want to speak to the Eqaya team before getting started?',
    sections: [
      {
        heading: 'General enquiries',
        body: 'For platform questions, partnership conversations, media enquiries, or general support, email info@eqaya.com and we will route your message to the right person.'
      },
      {
        heading: 'Project support',
        body: 'If you already have an Eqaya account, include your project name, the city or area in Zimbabwe, and a short summary of what you need help with so our team can respond faster.'
      },
      {
        heading: 'Social media',
        body: 'You can also follow Eqaya on Facebook, Instagram, and X for platform updates, construction tips, and stories from the community.'
      }
    ],
    cta: 'Create an account'
  },
  faq: {
    title: 'Frequently Asked Questions',
    intro: 'Quick answers to the questions people usually ask before using Eqaya.',
    sections: [
      {
        heading: 'Is Eqaya only for people outside Zimbabwe?',
        body: 'No. Eqaya is especially useful for the Zimbabwean diaspora, but anyone who wants verified contractors, independent progress checks, and clearer milestone control can use the platform.'
      },
      {
        heading: 'Are contractors verified?',
        body: 'Eqaya is built around verified contractor profiles, project history, documentation, and reputation signals so clients can compare options with more confidence before hiring.'
      },
      {
        heading: 'How does work verification help?',
        body: 'Independent verifiers can physically visit a site, capture photo evidence, and provide a report before a client approves a milestone or releases payment.'
      },
      {
        heading: 'When are payments released?',
        body: 'Eqaya is designed around milestone approval. Clients can review progress and verification evidence before approving payment according to the agreed project terms.'
      },
      {
        heading: "Why can't work start on a stage until it's funded?",
        body: "Eqaya guarantees the contractor's payment — that guarantee only works if the money is really there. Before any work stage begins, the client must lock that stage's amount into the project wallet (escrow); the platform blocks starting an unfunded stage. The contractor can see the money is secured before buying materials or paying workers, and the client's money is only released when they approve the completed stage. If you see \"work can't start yet\", the current stage still needs to be funded."
      },
      {
        heading: 'Does the client have to fund the whole project upfront?',
        body: "No. After a bid is accepted, the client and contractor agree a payment-stage breakdown sized to what the client can fund at a time — a small job can be one stage, bigger jobs are broken down (for example siting & foundation first). The client only ever funds the stage that's about to start, never the whole total."
      }
    ],
    cta: 'Get started free'
  },
  privacy: {
    title: 'Privacy Policy',
    intro: 'This privacy notice explains the types of information Eqaya collects and how we use it to operate the platform. Last updated: August 2026.',
    sections: [
      {
        heading: 'Information we collect',
        body: 'Eqaya may collect account details, contact information (including phone numbers), project information, uploaded documents, site photos, messages, payment-related records, device information, and verification evidence submitted through the platform.'
      },
      {
        heading: 'How we use information',
        body: 'We use information to create and secure accounts, match clients with contractors or verifiers, manage projects, support payments, provide customer support, improve platform reliability, prevent fraud, and meet legal or compliance obligations.'
      },
      {
        heading: 'Messaging and WhatsApp',
        body: 'If you connect your WhatsApp number, Eqaya delivers your platform conversations to you on WhatsApp and relays your replies back to the other party through Eqaya’s official number. Your phone number is never shown to other users. Message content is stored on your Eqaya account like any other platform conversation. You can stop WhatsApp delivery at any time by replying STOP.'
      },
      {
        heading: 'Sharing and protection',
        body: 'We share information only where needed to deliver the platform, such as with service providers, payment processors, messaging providers, contractors, verifiers, or legal authorities where required. We use technical and operational safeguards to protect platform data. We never sell your personal information.'
      },
      {
        heading: 'Your choices',
        body: 'Users can update account information, manage communication preferences where available, and contact Eqaya for questions about personal information, account access, or deletion. Email info@eqaya.com for any privacy request.'
      }
    ],
    cta: 'Back to home'
  },
  terms: {
    title: 'Terms of Service',
    intro: 'These terms govern your use of the Eqaya platform. By creating an account or using Eqaya, you agree to them. Last updated: August 2026.',
    sections: [
      {
        heading: 'What Eqaya is',
        body: 'Eqaya is a marketplace and trust platform that connects property owners with contractors, independent verifiers, and related service providers, and provides tools such as project management, milestone tracking, messaging, quotations, and escrow-protected payments. Eqaya is not a construction company: contractors and verifiers are independent providers responsible for their own work, and Eqaya does not guarantee the outcome of any project.'
      },
      {
        heading: 'Accounts',
        body: 'You must provide accurate information when registering, keep your login details secure, and use the platform only for lawful purposes. Certain roles (such as contractors and verifiers) require approval and supporting documents before full access is granted. Eqaya may suspend or close accounts that breach these terms, misrepresent identity or credentials, or attempt to defraud other users.'
      },
      {
        heading: 'Payments, escrow, and fees',
        body: 'Payments made through Eqaya (project milestones, Property Checks, call-out fees, and invoices) are processed by our payment providers and, where applicable, held in escrow until the agreed work is confirmed. Eqaya charges a platform commission on amounts released to providers, and service fees for products such as Property Checks; applicable fees are shown before you pay. Released amounts, refunds, and disputes are handled according to the flow shown in the product at the time of payment.'
      },
      {
        heading: 'Work quality and disputes',
        body: 'Agreements for work are between the client and the provider. Eqaya provides tools — verification visits, photo evidence, milestone approvals — to help both sides transact with confidence, but does not supervise work and is not a party to the underlying contract. If something goes wrong, raise a dispute through the platform; Eqaya may review evidence and direct held funds according to its dispute process, acting in good faith.'
      },
      {
        heading: 'Messaging and communications',
        body: 'By using Eqaya you consent to receiving service communications by email, in-app notification, and — if you connect your number — WhatsApp. Conversations between users are stored on the platform. Do not use Eqaya messaging for unlawful, abusive, or deceptive content; Eqaya may act on reports of misuse.'
      },
      {
        heading: 'Content and conduct',
        body: 'You retain rights to content you upload (photos, documents, portfolio items) and grant Eqaya a licence to store and display it as needed to operate the platform. Do not upload content you have no right to share. Attempting to move transactions off-platform to avoid fees or protections, misrepresenting work, or manipulating reviews and verification evidence are breaches of these terms.'
      },
      {
        heading: 'Reputation on Eqaya (contractors and verifiers)',
        body: 'Your public record on Eqaya — reviews, ratings, the number of jobs completed, and the verified badge — is built only from work paid through the Eqaya project wallet. A client can only review you after a stage has been paid to you through Eqaya. Work you agree with an Eqaya client and take off the platform for cash or direct payment does not count towards your record, is not covered by the Eqaya Guarantee, and cannot be reviewed or disputed on Eqaya. You may talk to clients by phone or in person; when you agree a price, send the quotation on Eqaya so the job is protected and counts.'
      },
      {
        heading: 'Liability',
        body: 'Eqaya provides the platform “as is” and, to the maximum extent permitted by law, is not liable for indirect or consequential losses, or for the acts or omissions of independent contractors, verifiers, or other users. Nothing in these terms excludes liability that cannot be excluded by law.'
      },
      {
        heading: 'Changes and contact',
        body: 'We may update these terms as the platform evolves; material changes will be communicated through the platform. Continued use after changes take effect constitutes acceptance. Questions about these terms: info@eqaya.com.'
      }
    ],
    cta: 'Back to home'
  }
};

export default function PublicInfoPage({ type }) {
  const navigate = useNavigate();
  const content = pageContent[type] || pageContent.about;

  const handleCta = () => {
    if (type === 'privacy' || type === 'terms') navigate('/');
    else navigate('/register');
  };

  return (
    <Box sx={{ bgcolor: '#fff', color: text.primary, fontFamily: FONT, minHeight: '100%' }}>
      <Box sx={{
        bgcolor: DEEP_TEAL,
        color: '#fff',
        py: { xs: 7, md: 10 },
        background: `linear-gradient(135deg, ${DEEP_TEAL} 0%, ${TEAL_DARK} 100%)`,
      }}>
        <Container maxWidth="md">
          <Typography component="h1" sx={{ fontFamily: FONT, fontWeight: 800, fontSize: { xs: '2.3rem', md: '3.5rem' }, lineHeight: 1.1, mb: 2 }}>
            {content.title}
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, lineHeight: 1.8, color: 'rgba(255,255,255,0.82)', maxWidth: 760 }}>
            {content.intro}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
        <Stack spacing={3}>
          {content.sections.map((section) => (
            <Box key={section.heading} sx={{ borderBottom: `1px solid ${border.light}`, pb: 3 }}>
              <Typography component="h2" sx={{ fontFamily: FONT, fontWeight: 800, color: DEEP_TEAL, fontSize: '1.35rem', mb: 1 }}>
                {section.heading}
              </Typography>
              <Typography sx={{ color: text.secondary, lineHeight: 1.85, fontSize: '1rem' }}>
                {section.body}
              </Typography>
            </Box>
          ))}
        </Stack>

        {type === 'contact' && (
          <Box sx={{ mt: 4, p: 3, border: `1px solid ${border.light}`, borderRadius: 2, bgcolor: '#F5F7F6' }}>
            <Typography sx={{ fontWeight: 800, color: DEEP_TEAL, mb: 1 }}>Email Eqaya</Typography>
            <Typography sx={{ color: text.secondary, lineHeight: 1.7 }}>
              Email us at <Link href="mailto:info@eqaya.com" sx={{ color: DEEP_TEAL, fontWeight: 700 }}>info@eqaya.com</Link>.
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleCta}
            sx={{ bgcolor: DEEP_TEAL, '&:hover': { bgcolor: TEAL_DARK }, fontWeight: 800, textTransform: 'none', px: 3, py: 1.2 }}
          >
            {content.cta}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/contact')}
            sx={{ display: type === 'contact' ? 'none' : 'inline-flex', borderColor: DEEP_TEAL, color: DEEP_TEAL, textTransform: 'none', fontWeight: 700, px: 3, py: 1.2 }}
          >
            Contact us
          </Button>
        </Box>

        {type === 'contact' && (
          <Box sx={{ mt: 4, color: text.secondary }}>
            <Link href="https://www.facebook.com/profile.php?id=61590825145207" target="_blank" rel="noopener noreferrer" sx={{ color: DEEP_TEAL, mr: 2 }}>Facebook</Link>
            <Link href="https://www.instagram.com/eqayainc/" target="_blank" rel="noopener noreferrer" sx={{ color: DEEP_TEAL, mr: 2 }}>Instagram</Link>
            <Link href="https://x.com/EqayaInc" target="_blank" rel="noopener noreferrer" sx={{ color: DEEP_TEAL }}>X</Link>
          </Box>
        )}
      </Container>
    </Box>
  );
}
